import { useState } from 'react';
import { Link } from 'react-router-dom';
import client from '../api/client';
import SocialLinks from './Global/SocialLinks';
import seo from './SEO/data';
import { useAlert } from '../context/AlertContext';

const navigation = {
	company: [
		{ name: 'About Gabriel', href: '/about' },
		{ name: 'Why choose Gabe', href: '/why-choose-gabe' },
		{ name: 'Ask Gabe', href: '/ask-gabe' },
		{ name: 'Contact', href: '/contact' },
		{ name: 'Admin', href: '/admin' },
	],
	learn: [
		{ name: 'Resource center', href: '/resources' },
		{ name: 'Maryland incentives', href: '/maryland-solar-incentives' },
		{ name: 'Powerwall vs Enphase', href: '/resources/powerwall-vs-enphase' },
		{ name: 'REC vs Canadian Solar', href: '/resources/rec-vs-canadian-solar' },
		{ name: 'How solar works', href: '/resources/how-solar-works-with-gabe' },
	],
	utilities: [
		{ name: 'Solar with BGE', href: '/solar-with/bge' },
		{ name: 'Solar with Pepco', href: '/solar-with/pepco' },
		{ name: 'Solar with Potomac Edison', href: '/solar-with/potomac-edison' },
		{ name: 'Solar with Delmarva', href: '/solar-with/delmarva' },
	],
	areas: [
		{ name: 'Maryland', href: '/service-areas/maryland' },
		{ name: 'Pennsylvania', href: '/service-areas/pennsylvania' },
		{ name: 'Virginia', href: '/service-areas/virginia' },
		{ name: 'Washington DC', href: '/service-areas/dc' },
	],
};

export default function Footer() {
	const [formData, setFormData] = useState({});
	const { setAlert } = useAlert();

	const handleChange = (e) => {
		e.preventDefault();
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			const res = await client.post('/customer', {
				email: formData.email,
			});
			if (res) {
				setFormData({});
				setAlert('success', 'Thank you for subscribing!');
			}
		} catch (error) {
			console.error(error);
			return setAlert('error', 'Error subscribing. Please try again.');
		}
	};

	return (
		<footer
			className='bg-ink-900 text-white'
			aria-labelledby='footer-heading'
		>
			<h2
				id='footer-heading'
				className='sr-only'
			>
				Footer
			</h2>
			<div className='mx-auto max-w-7xl px-6 pb-10 pt-20 sm:pt-24 lg:px-8'>
				<div className='grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8'>
					<div className='lg:col-span-5'>
						<div className='flex items-center gap-3'>
							<span className='font-display text-2xl font-medium tracking-tight'>
								{seo.companyName}
							</span>
						</div>
						<p className='mt-4 max-w-md text-sm leading-6 text-white/65'>
							Personal, transparent solar guidance for homeowners and businesses
							across Maryland, Pennsylvania, Virginia, and DC.
						</p>
						{/* areas list is driven by serviceAreas data — DE intentionally omitted */}
						<div className='mt-10'>
							<h3 className='text-xs font-semibold uppercase tracking-[0.18em] text-primary-400'>
								Stay in the loop
							</h3>
							<p className='mt-3 text-sm text-white/65'>
								Occasional updates on incentives, rates, and new tech.
							</p>
							<form
								className='mt-4 sm:flex sm:max-w-md'
								onSubmit={handleSubmit}
							>
								<label
									htmlFor='email'
									className='sr-only'
								>
									Email address
								</label>
								<input
									type='email'
									name='email'
									onChange={handleChange}
									id='email'
									autoComplete='email'
									value={formData.email || ''}
									required
									className='w-full min-w-0 appearance-none rounded-full border-0 bg-white/10 px-4 py-2.5 text-sm text-white placeholder:text-white/50 ring-1 ring-inset ring-white/20 focus:ring-2 focus:ring-inset focus:ring-primary-400 sm:w-64'
									placeholder='Enter your email'
								/>
								<div className='mt-3 sm:ml-3 sm:mt-0 sm:flex-shrink-0'>
									<button
										type='submit'
										className='flex w-full items-center justify-center rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-600 transition-colors'
									>
										Subscribe
									</button>
								</div>
							</form>
						</div>
					</div>

					<div className='lg:col-span-4 grid grid-cols-2 gap-8 sm:grid-cols-3'>
						<div>
							<h3 className='text-xs font-semibold uppercase tracking-[0.18em] text-primary-400'>
								Company
							</h3>
							<ul className='mt-6 space-y-3'>
								{navigation.company.map((item) => (
									<li key={item.name}>
										<Link
											to={item.href}
											className='text-sm text-white/65 hover:text-primary-400 transition-colors'
										>
											{item.name}
										</Link>
									</li>
								))}
							</ul>
						</div>
						<div>
							<h3 className='text-xs font-semibold uppercase tracking-[0.18em] text-primary-400'>
								Learn
							</h3>
							<ul className='mt-6 space-y-3'>
								{navigation.learn.map((item) => (
									<li key={item.name}>
										<Link
											to={item.href}
											className='text-sm text-white/65 hover:text-primary-400 transition-colors'
										>
											{item.name}
										</Link>
									</li>
								))}
							</ul>
						</div>
						<div>
							<h3 className='text-xs font-semibold uppercase tracking-[0.18em] text-primary-400'>
								By utility
							</h3>
							<ul className='mt-6 space-y-3'>
								{navigation.utilities.map((item) => (
									<li key={item.name}>
										<Link
											to={item.href}
											className='text-sm text-white/65 hover:text-primary-400 transition-colors'
										>
											{item.name}
										</Link>
									</li>
								))}
							</ul>
						</div>
					</div>

					<div className='lg:col-span-3'>
						<h3 className='text-sm font-semibold text-white'>Reach out</h3>
						<ul className='mt-6 space-y-3 text-sm text-white/65'>
							<li>
								<a
									href={`tel:${seo.company.phone}`}
									className='hover:text-primary-400 transition-colors'
								>
									{seo.company.phone}
								</a>
							</li>
							{seo.company.email && (
								<li>
									<a
										href={`mailto:${seo.company.email}`}
										className='hover:text-primary-400 transition-colors'
									>
										{seo.company.email}
									</a>
								</li>
							)}
							<li>
								{seo.company.address.city}, {seo.company.address.state}
							</li>
							<li className='pt-2'>
								<SocialLinks footer={true} />
							</li>
						</ul>
					</div>
				</div>

				<div className='mt-16 border-t border-white/10 pt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
					<p className='text-xs leading-5 text-white/60'>
						&copy; {new Date().getFullYear()} {seo.companyName}. All rights
						reserved.
					</p>
					<p className='text-xs leading-5 text-white/60'>
						Serving MD · PA · VA · DC
					</p>
				</div>
			</div>
		</footer>
	);
}
